import type { RxCollection } from 'rxdb';
import type {
    MediaDocType,
    MediaGroupDocType,
    MediaLinkDocType
} from '@ast-data/shared';
import { mediaShownBy } from '../../media/mediaLinks';
import { groupSlots, type GroupSlot } from '../../media/mediaGroups';
import { authFetch } from '../../../utils/authFetch';
import { getCache, getPending, saveCache } from '../../media/blobStore';
import { compressImage } from '../../media/compressImage';
import { loadMediaBlob } from '../../media/loadMediaBlob';
import type { SceneData } from '../../media/sketch/types';

/**
 * Getting the pictures of a record INTO a PDF — the part every sheet does the
 * same way, whatever the record is.
 *
 * react-pdf's <Image> wants a data-URL, so every binary is read out of the local
 * cache (or fetched) and base64-encoded before the document is built. That is
 * the expensive half of any export with drawings in it, and the rules for doing
 * it without melting the tab — a concurrency ceiling, and fetching nothing the
 * sheet will not print — live here so there is one answer, not two.
 */

/** One image belonging to a record, resolved for embedding in the PDF. */
export interface ExportMedia {
    id: string;
    kind: 'photo' | 'sketch';
    caption: string;
    mimeType: string;
    /** Data-URL of the image — for a sketch drawn afresh from its scene — or
     *  null if the binary isn't available on this device yet. */
    dataUrl: string | null;
    /** Parsed sketch scene (drives the Legende); null for photos / bad JSON. */
    scene: SceneData | null;
    /**
     * Width ÷ height, for sizing the frame the image is printed in. A sketch
     * carries it in its scene; a photo has it nowhere (the media document
     * stores no pixel size), so it is MEASURED off the decoded image. Null when
     * it could not be read — the layout then uses its tallest frame.
     */
    aspect: number | null;
    /**
     * The grid this picture prints in on THIS record's sheet, if any. Per
     * record, not per picture: a linked sketch can be grouped in one
     * description and stand alone in another.
     */
    groupSlot?: GroupSlot;
}

/**
 * The aspect of a decoded image, or null if it will not load. Only PHOTOS pay
 * for the decode: a sketch carries its aspect in its scene.
 */
async function imageAspect(dataUrl: string): Promise<number | null> {
    try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const element = new Image();
            element.onload = () => resolve(element);
            element.onerror = () => reject(new Error('decode failed'));
            element.src = dataUrl;
        });
        if (!image.naturalWidth || !image.naturalHeight) {
            return null;
        }
        return image.naturalWidth / image.naturalHeight;
    } catch {
        return null;
    }
}

/** A picture under the record that SHOWS it — its origin, or a link's owner. */
export interface PlacedMedia {
    ownerId: string;
    media: MediaDocType;
    groupSlot?: GroupSlot;
}

/**
 * Every picture these records show — their own and those linked into them —
 * each placed under the record that shows it, with the grid it is grouped in
 * there. A linked sketch prints on the sheet of every description it appears
 * in, as it appears in the app.
 */
export async function findPlacedMedia(
    mediaCollection: RxCollection<MediaDocType>,
    linkCollection: RxCollection<MediaLinkDocType>,
    groupCollection: RxCollection<MediaGroupDocType>,
    ownerIds: readonly string[]
): Promise<PlacedMedia[]> {
    if (ownerIds.length === 0) {
        return [];
    }
    const json = <T>(docs: { toJSON(): unknown }[]) =>
        docs.map((doc) => doc.toJSON() as T);
    const ownMedia = json<MediaDocType>(
        await mediaCollection
            .find({ selector: { ownerId: { $in: [...ownerIds] } } })
            .exec()
    );
    const links = json<MediaLinkDocType>(
        await linkCollection
            .find({ selector: { ownerId: { $in: [...ownerIds] } } })
            .exec()
    );
    const linkedMedia = links.length
        ? json<MediaDocType>(
              await mediaCollection
                  .find({
                      selector: {
                          id: { $in: links.map((link) => link.mediaId) }
                      }
                  })
                  .exec()
          )
        : [];
    const groups = json<MediaGroupDocType>(
        await groupCollection
            .find({ selector: { ownerId: { $in: [...ownerIds] } } })
            .exec()
    );
    return ownerIds.flatMap((ownerId) => {
        const shown = mediaShownBy(
            ownMedia.filter((media) => media.ownerId === ownerId),
            links.filter((link) => link.ownerId === ownerId),
            linkedMedia
        );
        const slots = groupSlots(
            groups.filter((group) => group.ownerId === ownerId),
            new Set(shown.map(({ media }) => media.id))
        );
        return shown.map(({ media }) => ({
            ownerId,
            media,
            groupSlot: slots.get(media.id)
        }));
    });
}

/**
 * How many pictures the sheets print without their image — the binary is not
 * on this device and the server could not be asked (offline) or has none yet.
 * Each picture once, however many sheets show it.
 */
export function unavailableMediaCount(media: Iterable<ExportMedia>): number {
    const missing = new Set<string>();
    for (const item of media) {
        if (!item.dataUrl) {
            missing.add(item.id);
        }
    }
    return missing.size;
}

/** How many media binaries to resolve at once (see resolveExportMedia). */
const MEDIA_CONCURRENCY = 6;

/**
 * The long edge any picture goes into the PDF at — a photo scaled down, a
 * sketch drawn at it. Its frame on the sheet is well under A4's width, which at
 * 300 dpi is about 2000 px; the full 4032 is four times the pixels to encode
 * and embed, for nothing the paper can show.
 */
export const PDF_IMAGE_DIMENSION = 2000;

/**
 * A photo as the PDF prints it: at most PDF_IMAGE_DIMENSION on its long edge,
 * through the same downscale an upload takes (orientation included). Null when
 * the binary is not on this device.
 */
async function photoDataUrl(id: string): Promise<string | null> {
    const original = await loadMediaBlob(id);
    if (!original) {
        return null;
    }
    const { blob } = await compressImage(original, PDF_IMAGE_DIMENSION);
    return blobToDataUrl(blob);
}

/**
 * A sketch is drawn one at a time, whatever MEDIA_CONCURRENCY allows: each
 * drawing holds a stage of nine full-size canvases (~100 MB at 2000 px), and
 * six of those at once is exactly the memory spike the ceiling is there for.
 */
let sketchQueue: Promise<unknown> = Promise.resolve();

/**
 * A sketch as the PDF prints it: DRAWN AGAIN FROM ITS SCENE, like a download,
 * so it shows the pens as they are today (see currentPenColor) rather than as
 * whenever its stored image was last saved.
 *
 * The stored image is the fallback: when there is no scene, when the render
 * fails, and when the photo under the sketch is not on this device — a drawing
 * without its photo is worse than one in yesterday's colours. Only with no
 * stored image either does the photo-less drawing print.
 */
function sketchDataUrl(media: MediaDocType): Promise<string | null> {
    const run = async () => {
        if (!media.scene) {
            // Pre-scene sketches: the stored image is all there is.
            return resolveMediaDataUrl(media.id);
        }
        const { renderSceneToImage } =
            await import('../../media/sketch/renderScene');
        const rendered = await renderSceneToImage(
            media.scene,
            PDF_IMAGE_DIMENSION
        ).catch(() => null);
        if (rendered && !rendered.missingBackground) {
            return blobToDataUrl(rendered.blob);
        }
        return (
            (await resolveMediaDataUrl(media.id)) ??
            (rendered ? blobToDataUrl(rendered.blob) : null)
        );
    };
    const result = sketchQueue.then(run, run);
    sketchQueue = result.catch(() => null);
    return result;
}

/** `Promise.all` with a ceiling — the same work, without the memory spike. */
async function forEachLimited<Item>(
    items: readonly Item[],
    limit: number,
    run: (item: Item) => Promise<void>
): Promise<void> {
    let next = 0;
    const workers = Array.from(
        { length: Math.min(limit, items.length) },
        async () => {
            while (next < items.length) {
                const index = next;
                next += 1;
                await run(items[index]);
            }
        }
    );
    await Promise.all(workers);
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

/**
 * Offline-first (local cache → local pending → server), mirroring
 * useMediaBlobUrl but returning the data-URL react-pdf needs. Null when the
 * binary isn't available.
 */
export async function resolveMediaDataUrl(id: string): Promise<string | null> {
    const cached = await getCache(id);
    if (cached) {
        return blobToDataUrl(cached);
    }
    const pending = await getPending(id);
    if (pending) {
        return blobToDataUrl(pending.blob);
    }
    try {
        const response = await authFetch(`/api/media/${id}/blob`);
        if (response.ok) {
            const blob = await response.blob();
            await saveCache(id, blob);
            return blobToDataUrl(blob);
        }
    } catch {
        // Offline / not uploaded yet → no image.
    }
    return null;
}

/** Parse a media doc's `scene` JSON string into a SceneData (or null). */
export function parseScene(raw: string): SceneData | null {
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw) as SceneData;
    } catch {
        return null;
    }
}

/**
 * The photos a sketch was drawn ON, per owner. Such a photo is already on the
 * sheet, inside its sketch, and printing it again says the same thing twice.
 *
 * Scoped per OWNER rather than across the export: a sketch shows its background
 * only where the sketch is, so the same photo referenced from another record is
 * unrepresented there and must print.
 */
function sketchedOverByOwner(
    placed: readonly PlacedMedia[]
): Map<string, Set<string>> {
    const sketchedOver = new Map<string, Set<string>>();
    for (const { ownerId, media } of placed) {
        const background =
            media.kind === 'sketch' && parseScene(media.scene)?.bg;
        if (background) {
            const seen = sketchedOver.get(ownerId) ?? new Set<string>();
            seen.add(background);
            sketchedOver.set(ownerId, seen);
        }
    }
    return sketchedOver;
}

/**
 * Every printable picture of these media docs, grouped by owner and
 * `createdAt`-ascending, the order they were taken in.
 *
 * A whole-project export can span thousands of images; resolving them at once
 * would hold every binary AND its ~1.33x base64 string in memory. So this walks
 * them a few at a time, and fetches nothing the sheet will not print.
 */
export async function resolveExportMedia(
    placed: readonly PlacedMedia[]
): Promise<(ownerId: string) => ExportMedia[]> {
    const sketchedOver = sketchedOverByOwner(placed);
    const isPrinted = ({ ownerId, media }: PlacedMedia) =>
        media.kind === 'sketch' || !sketchedOver.get(ownerId)?.has(media.id);
    // A picture linked into several printed records is resolved once.
    const printable = [
        ...new Map(
            placed
                .filter(isPrinted)
                .map(({ media }) => [media.id, media] as const)
        ).values()
    ];
    const resolved = new Map<string, ExportMedia>();
    await forEachLimited(printable, MEDIA_CONCURRENCY, async (media) => {
        const isSketch = media.kind === 'sketch';
        const dataUrl = isSketch
            ? await sketchDataUrl(media)
            : await photoDataUrl(media.id);
        const scene = isSketch ? parseScene(media.scene) : null;
        resolved.set(media.id, {
            id: media.id,
            kind: isSketch ? 'sketch' : 'photo',
            caption: media.caption,
            mimeType: media.mimeType,
            dataUrl,
            scene,
            // The sketch knows its own shape; a photo has to be asked.
            aspect: isSketch
                ? (scene?.aspect ?? null)
                : dataUrl
                  ? await imageAspect(dataUrl)
                  : null
        });
    });

    // In the order the gallery shows them: own pictures, then linked ones.
    const byOwner = new Map<string, ExportMedia[]>();
    for (const item of placed) {
        const entry = isPrinted(item) ? resolved.get(item.media.id) : undefined;
        if (!entry) {
            continue;
        }
        byOwner.set(item.ownerId, [
            ...(byOwner.get(item.ownerId) ?? []),
            item.groupSlot ? { ...entry, groupSlot: item.groupSlot } : entry
        ]);
    }
    return (ownerId: string) => byOwner.get(ownerId) ?? [];
}
